import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, DatePicker, message, Popconfirm, Typography, Upload, Space, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, FilePdfOutlined } from '@ant-design/icons';
import api, { getVehicles, createVehicle, updateVehicle, deleteVehicle, imagepath } from '../api';
import moment from 'moment';
moment.locale('en');

const Vehicles = () => {
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [modalVisible, setModalVisible] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form] = Form.useForm();

    const fetchVehicles = async () => {
        setLoading(true);
        const res = await getVehicles();
        setLoading(false);
        if (res.success) {
            console.log(res.data.data)
            setVehicles(Array.isArray(res.data.data) ? res.data.data : []);
        } else {
            message.error(res.message || 'Failed to fetch vehicles');
        }
    };

    useEffect(() => {
        fetchVehicles();
    }, []);

    const openModal = (record = null) => {
        setEditing(record);
        setModalVisible(true);
        if (record) {
            form.setFieldsValue({
                ...record,
                expiry_date: moment(record.expiry_date),
                registration_card: [],
                registration_card_front: [],
                registration_card_back: [],
                insurance: [],
                test_report: []
            });
        } else {
            form.resetFields();
            form.setFieldsValue({
                registration_card: [],
                registration_card_front: [],
                registration_card_back: [],
                insurance: [],
                test_report: []
            });
        }
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            const formData = new FormData();

            formData.append('vehicle', values.vehicle);
            formData.append('reg_no', values.reg_no);
            formData.append('expiry_date', values.expiry_date.format('YYYY-MM-DD'));
            if (values.salik_tag_no) formData.append('salik_tag_no', values.salik_tag_no);
            if (values.salik_account_no) formData.append('salik_account_no', values.salik_account_no);

            const fileFields = ['registration_card', 'registration_card_front', 'registration_card_back', 'insurance', 'test_report'];
            fileFields.forEach(field => {
                if (values[field] && values[field][0] && values[field][0].originFileObj) {
                    formData.append(field, values[field][0].originFileObj);
                } else if (values[field] && values[field].length === 0 && editing && editing[field]) {
                    // If file list is empty and we had a file before, it means it was removed (if supported by logic below)
                    // For now, let's add a removal flag if needed, but the current UI uses "View Current File" separate from Upload
                }
            });

            let res;
            if (editing) {
                res = await updateVehicle(editing.id, formData);
            } else {
                res = await createVehicle(formData);
            }
            if (res.success) {
                message.success(editing ? 'Vehicle updated' : 'Vehicle created');
                setModalVisible(false);
                fetchVehicles();
            } else {
                message.error(res.message || 'Operation failed');
            }
        } catch (err) {
            console.error('Validation error:', err);
        }
    };

    const handleRemoveFile = async (field) => {
        try {
            const token = localStorage.getItem('token');
            const res = await api.post(`/vehicles/${editing.id}?_method=PUT`, { [field]: 'REMOVE' }, { headers: { Authorization: `Bearer ${token}` } });
            if (res.success) {
                message.success('File removed');
                const updatedEditing = { ...editing, [field]: null };
                setEditing(updatedEditing);
                fetchVehicles();
            } else {
                message.error(res.message || 'Removal failed');
            }
        } catch (err) {
            message.error('Error removing file');
        }
    };

    const handleDelete = async (id) => {
        const res = await deleteVehicle(id);
        if (res.success) {
            message.success('Vehicle deleted');
            fetchVehicles();
        } else {
            message.error(res.message || 'Delete failed');
        }
    };

    const columns = [
        {
            title: 'S.No',
            key: 'sno',
            width: 60,
            render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1
        },
        { title: 'Vehicle', dataIndex: 'vehicle', key: 'vehicle' },
        { title: 'Reg No', dataIndex: 'reg_no', key: 'reg_no' },
        { title: 'Salik Tag No', dataIndex: 'salik_tag_no', key: 'salik_tag_no' },
        { title: 'Salik Account No', dataIndex: 'salik_account_no', key: 'salik_account_no' },
        {
            title: 'Expiry Date',
            dataIndex: 'expiry_date',
            key: 'expiry_date',
            render: d => {
                const today = moment();
                const exp = moment(d);
                let color = 'green';
                if (exp.isBefore(today, 'day')) color = 'red';
                else if (exp.diff(today, 'days') <= 30) color = 'orange';
                return <Typography.Text style={{ color }}>{exp.format('DD/MM/YYYY')}</Typography.Text>;
            }
        },
        {
            title: 'Documents',
            key: 'documents',
            render: (_, record) => {
                const docs = [
                    { key: 'registration_card', label: 'Reg. Card' },
                    { key: 'registration_card_front', label: 'Reg. Card (Front)' },
                    { key: 'registration_card_back', label: 'Reg. Card (Back)' },
                    { key: 'insurance', label: 'Insurance' },
                    { key: 'test_report', label: 'Test Report' },
                ];

                return (
                    <Space size={[0, 4]} wrap>
                        {docs.map(doc => record[doc.key] ? (
                            <Tag
                                key={doc.key}
                                color="blue"
                                icon={<FilePdfOutlined />}
                                style={{ cursor: 'pointer' }}
                                onClick={() => window.open(`${imagepath}${record[doc.key]}`, '_blank')}
                            >
                                {doc.label}
                            </Tag>
                        ) : null)}
                    </Space>
                );
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 160,
            render: (_, record) => (
                <>
                    <Button icon={<EditOutlined />} onClick={() => openModal(record)} style={{ marginRight: 8 }} />
                    <Popconfirm title="Are you sure to delete this vehicle?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
                        <Button icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                </>
            ),
        },
    ];
    console.log(vehicles)
    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <h2>Vehicles</h2>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>Add Vehicle</Button>
            </div>
            <Table
                rowKey="id"
                columns={columns}
                dataSource={vehicles}
                loading={loading}
                bordered
                pagination={pagination}
                onChange={(p) => setPagination(p)}
            />
            <Modal
                title={editing ? 'Edit Vehicle' : 'Add Vehicle'}
                open={modalVisible}
                onOk={handleOk}
                onCancel={() => setModalVisible(false)}
                okText={editing ? 'Update' : 'Create'}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="vehicle" label="Vehicle" rules={[{ required: true, message: 'Please enter vehicle name' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="reg_no" label="Registration No" rules={[{ required: true, message: 'Please enter registration number' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="salik_tag_no" label="Salik Tag No">
                        <Input />
                    </Form.Item>
                    <Form.Item name="salik_account_no" label="Salik Account No">
                        <Input />
                    </Form.Item>
                    <Form.Item name="expiry_date" label="Expiry Date" rules={[
                        { required: true, message: 'Please select expiry date' },
                        {
                            validator: (_, value) => {
                                if (value && moment(value).isBefore(moment().startOf('day'))) {
                                    return Promise.reject(new Error('Expiry date cannot be in the past'));
                                }
                                return Promise.resolve();
                            }
                        }
                    ]}>
                        <DatePicker 
                            style={{ width: '100%' }} 
                            format="DD/MM/YYYY" 
                            disabledDate={(current) => current && current.isBefore(moment().startOf('day'))}
                        />
                    </Form.Item>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        {[
                            { name: 'registration_card_front', label: 'Registration Card (Front)' },
                            { name: 'registration_card_back', label: 'Registration Card (Back)' },
                            { name: 'insurance', label: 'Insurance' },
                            { name: 'test_report', label: 'Test Report' },
                        ].map(doc => (
                            <div key={doc.name}>
                                <Form.Item
                                    name={doc.name}
                                    label={doc.label}
                                    valuePropName="fileList"
                                    getValueFromEvent={e => (Array.isArray(e) ? e : e && e.fileList)}
                                    extra={editing && editing[doc.name] ? (
                                        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Tag color="green" icon={<FilePdfOutlined />} style={{ cursor: 'pointer', margin: 0 }} onClick={() => window.open(`${imagepath}${editing[doc.name]}`, '_blank')}>
                                                View Current
                                            </Tag>
                                            <Popconfirm title="Remove this file?" onConfirm={() => handleRemoveFile(doc.name)}>
                                                <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                                            </Popconfirm>
                                        </div>
                                    ) : null}
                                >
                                    <Upload
                                        beforeUpload={(file) => {
                                            const isAllowedFormat = /\.(pdf|jpg|jpeg|png)$/i.test(file.name);
                                            if (!isAllowedFormat) {
                                                message.error(`"${file.name}" is an unsupported format. Only PDF, JPG, and PNG files are allowed.`);
                                                return Upload.LIST_IGNORE;
                                            }
                                            const isLt1M = file.size / 1024 / 1024 <= 1;
                                            if (!isLt1M) {
                                                message.error(`"${file.name}" exceeds the 1MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB). Please upload a file smaller than 1MB.`);
                                                return Upload.LIST_IGNORE;
                                            }
                                            return false;
                                        }}
                                        maxCount={1}
                                        accept=".pdf,.jpg,.jpeg,.png,.PDF,.JPG,.JPEG,.PNG"
                                    >
                                        <Button icon={<UploadOutlined />}>Select File</Button>
                                    </Upload>
                                    <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                                        Allowed formats: PDF, JPG, PNG (Max 1MB)
                                    </div>
                                </Form.Item>
                            </div>
                        ))}
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default Vehicles;
